/* Block 1 B2: owner-approved engraving alphabet and theme-side 50-character limit. */
(function () {
  'use strict';
  const LIMIT = 50;
  const latinLetter = /^\p{Script=Latin}$/u;
  const letter = /^\p{Letter}$/u;
  function isInitialBraceletPath(pathname) {
    return /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:collections\/[^/]+\/)?products\/initial-bracelet\/?$/i.test(pathname);
  }
  // LIMIT50-ALL: every product page is limit-only (50 UTF-16 units, NO alphabet filter, because
  // gift messages and names legitimately contain ! ? ' etc.), except initial-bracelet, which keeps
  // its own limit + alphabet-filter branch. The Textify field selector below decides what is capped.
  function isLimitOnlyPath(pathname) {
    return !isInitialBraceletPath(pathname) &&
      /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:collections\/[^/]+\/)?products\/[^/]+\/?$/i.test(pathname);
  }
  function sanitizeEngravingText(value) {
    if (typeof value !== 'string') return '';
    let followsLatin = false;
    return Array.from(value.normalize('NFC')).filter(function (character) {
      if (latinLetter.test(character) && letter.test(character)) {
        followsLatin = true;
        return true;
      }
      // Some Latin accents have no precomposed NFC letter (for example q + acute).
      if (followsLatin && /^\p{Mark}$/u.test(character) && /^\p{Diacritic}$/u.test(character)) return true;
      followsLatin = false;
      return /^[0-9 &.,/()\-]$/.test(character);
    }).join('');
  }
  function prepareEngravingText(value) {
    if (typeof value !== 'string') value = '';
    const normalized = value.normalize('NFC');
    const allowed = sanitizeEngravingText(value);
    // Match the textarea's native maxlength unit (UTF-16); never split a letter.
    let clean = '';
    for (const character of allowed) {
      if (clean.length + character.length > LIMIT) break;
      clean += character;
    }
    return { value: clean, rejected: allowed !== normalized, truncated: clean !== allowed };
  }
  function prepareLimitOnlyText(value) {
    if (typeof value !== 'string') value = '';
    // Same unit as the textarea's native maxlength (UTF-16); never split a surrogate pair.
    let clean = '';
    for (const character of value) {
      if (clean.length + character.length > LIMIT) break;
      clean += character;
    }
    return { value: clean, rejected: false, truncated: clean !== value };
  }
  if (typeof module === 'object' && module.exports) {
    module.exports = { sanitizeEngravingText, prepareEngravingText, prepareLimitOnlyText, isInitialBraceletPath, isLimitOnlyPath, LIMIT };
  }
  if (typeof document === 'undefined') return;
  // F10: scoped by the product path only (not by a theme id), so the script survives
  // a theme duplicate + publish and can be behaviour-tested on a test copy.
  // The bracelet keeps limit + alphabet filter; every other product page is limit-only.
  const limitOnly = !isInitialBraceletPath(location.pathname) && isLimitOnlyPath(location.pathname);
  if ((!limitOnly && !isInitialBraceletPath(location.pathname)) || window.byloroEngravingSanitizer) return;
  window.byloroEngravingSanitizer = true;
  const prepare = limitOnly ? prepareLimitOnlyText : prepareEngravingText;
  // Limit-only pages cap every Textify personalisation textarea on the page (the product's own
  // field and the gift-packaging upsell field), each handled by its own element, never by form order.
  const selector = limitOnly
    ? 'textarea[data-tb-app-fields][name^="properties["]'
    : 'textarea[data-tb-app-fields][name="properties[Personalisatie]"]';
  const composing = new WeakSet();
  const dutch = document.documentElement.lang.toLowerCase().startsWith('nl');
  const copy = dutch ? {
    help: limitOnly ? 'Maximaal 50 tekens.' : 'Maximaal 50 tekens. Toegestaan: Latijnse letters met accenten, 0–9, spaties en - & . , / ( ).',
    removed: 'Niet-toegestane tekens zijn verwijderd. Controleer je personalisatie.',
    // LIMIT50-FINAL (owner decision 22 Oct 2026): limit-only pages show exactly 'Maximaal 50 tekens.'
    truncated: limitOnly ? 'Maximaal 50 tekens.' : 'Maximaal 50 tekens. De extra tekst is niet toegevoegd. Controleer je personalisatie.',
    count: 'Aantal tekens'
  } : {
    help: limitOnly ? 'Maximum 50 characters.' : 'Maximum 50 characters. Allowed: Latin letters including accents, 0–9, spaces and - & . , / ( ).',
    removed: 'Unsupported characters were removed. Please check your personalization.',
    truncated: limitOnly ? 'Maximum 50 characters.' : 'Maximum 50 characters. Extra text was not added. Please check your personalization.',
    count: 'Character count'
  };
  // F5: the helper line and the "n/50" counter are hidden visually on the two-initials
  // bracelet page, but stay in the DOM as screen-reader text (aria-describedby). The
  // 50-character limit, the filtering and the status notices are unchanged.
  const visuallyHidden = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;';
  // One key per textarea. The field's own id when it is unique on the page (unchanged bracelet
  // behaviour), otherwise a generated one, so two textareas on one page never share help/status ids.
  const fieldKeys = new WeakMap();
  let fieldSeq = 0;
  function fieldKey(field) {
    if (fieldKeys.has(field)) return fieldKeys.get(field);
    const id = field.id;
    let unique = !!id;
    if (unique) {
      let seen = 0;
      document.querySelectorAll('[id]').forEach(function (el) { if (el.id === id) seen++; });
      unique = seen === 1;
    }
    const key = unique ? id : 'byloro-engraving-field-' + (++fieldSeq);
    fieldKeys.set(field, key);
    return key;
  }
  function addHelp(field) {
    const key = fieldKey(field);
    const helpId = key + '-byloro-engraving-help';
    const statusId = key + '-byloro-engraving-status';
    const counterId = key + '-byloro-engraving-counter';
    if (!composing.has(field)) field.maxLength = LIMIT;
    if (!document.getElementById(helpId)) {
      const container = document.createElement('div');
      container.dataset.byloroEngravingHelp = '';
      container.style.cssText = 'font-size:12px;line-height:1.5;margin:-4px 0 16px;overflow-wrap:anywhere;';
      const counter = document.createElement('p');
      counter.id = counterId;
      counter.dataset.byloroEngravingCounter = '';
      counter.style.cssText = visuallyHidden;
      const counterLabel = document.createElement('span');
      counterLabel.textContent = copy.count + ' ';
      const counterValue = document.createElement('span');
      counterValue.dataset.byloroEngravingCount = '';
      counter.append(counterLabel, counterValue);
      const help = document.createElement('p');
      help.id = helpId;
      help.textContent = copy.help;
      help.style.cssText = visuallyHidden;
      const status = document.createElement('p');
      status.id = statusId;
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      status.style.cssText = 'margin:4px 0 0;color:#8b251e;';
      container.append(counter, help, status);
      field.parentElement.insertAdjacentElement('afterend', container);
    }
    const describedBy = new Set((field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
    [helpId, statusId, counterId].forEach(function (id) { describedBy.add(id); });
    const description = [...describedBy].join(' ');
    if (field.getAttribute('aria-describedby') !== description) field.setAttribute('aria-describedby', description);
    const counter = document.getElementById(counterId);
    const counterValue = counter.querySelector('[data-byloro-engraving-count]');
    const count = field.value.length + '/' + LIMIT;
    if (counterValue && counterValue.textContent !== count) counterValue.textContent = count;
  }
  // N1: on a user edit (typing, paste, change, composition end) a value that is valid and was
  // not shortened clears the notice. Submit-time passes never clear (nothing was edited).
  function showNotice(field, result, clearWhenValid) {
    const status = document.getElementById(fieldKey(field) + '-byloro-engraving-status');
    if (!result.rejected && !result.truncated) {
      if (clearWhenValid && status && status.textContent !== '') status.replaceChildren();
      return;
    }
    // Replace the text node so repeated invalid attempts remain an aria-live update.
    status.replaceChildren(document.createTextNode([
      result.rejected ? copy.removed : '', result.truncated ? copy.truncated : ''
    ].filter(Boolean).join(' ')));
  }
  // Our own synthetic input/change events must not re-enter the handlers below, or the
  // re-check of the already shortened value would clear the notice we just showed.
  let internal = false;
  function notifyApp(field) {
    internal = true;
    try {
      field.dispatchEvent(new Event('input', { bubbles: true }));
      field.dispatchEvent(new Event('change', { bubbles: true }));
    } finally { internal = false; }
  }
  function sanitizeField(field, notify, clearWhenValid) {
    const previous = field.value;
    const result = prepare(previous);
    const start = field.selectionStart;
    const end = field.selectionEnd;
    if (result.value !== previous) {
      field.value = result.value;
      field.setSelectionRange(
        Math.min(prepare(previous.slice(0, start)).value.length, result.value.length),
        Math.min(prepare(previous.slice(0, end)).value.length, result.value.length)
      );
    }
    addHelp(field);
    showNotice(field, result, clearWhenValid);
    if (notify && result.value !== previous) notifyApp(field);
  }
  function insertText(field, text, event) {
    event.preventDefault();
    const start = field.selectionStart;
    const end = field.selectionEnd;
    const proposed = field.value.slice(0, start) + text + field.value.slice(end);
    const result = prepare(proposed);
    field.value = result.value;
    const caret = Math.min(prepare(proposed.slice(0, start + text.length)).value.length, result.value.length);
    field.setSelectionRange(caret, caret);
    addHelp(field);
    showNotice(field, result, true);
    notifyApp(field);
  }
  // Intercept before native maxlength truncation, so overflow always gets a notice
  // and decomposed paste is normalized before the browser counts its code units.
  document.addEventListener('paste', function (event) {
    if (event.target.matches?.(selector) && event.clipboardData) {
      insertText(event.target, event.clipboardData.getData('text/plain'), event);
    }
  }, true);
  document.addEventListener('beforeinput', function (event) {
    if (!event.target.matches?.(selector) || event.isComposing || composing.has(event.target)) return;
    if (event.cancelable && event.inputType.startsWith('insert')) {
      const text = event.dataTransfer?.getData('text/plain') ?? event.data;
      if (text !== null && text !== undefined) insertText(event.target, text, event);
      else if (event.inputType === 'insertLineBreak' || event.inputType === 'insertParagraph') insertText(event.target, '\n', event);
    }
  }, true);
  document.addEventListener('compositionstart', function (event) {
    if (!event.target.matches?.(selector)) return;
    composing.add(event.target);
    event.target.removeAttribute('maxlength');
  }, true);
  document.addEventListener('input', function (event) {
    if (internal) return;
    if (event.target.matches?.(selector) && !event.isComposing && !composing.has(event.target)) sanitizeField(event.target, false, true);
  }, true);
  document.addEventListener('compositionend', function (event) {
    if (!event.target.matches?.(selector)) return;
    composing.delete(event.target);
    sanitizeField(event.target, true, true);
  }, true);
  document.addEventListener('change', function (event) {
    if (internal) return;
    if (event.target.matches?.(selector) && !composing.has(event.target)) sanitizeField(event.target, true, true);
  }, true);
  function sanitizeBeforeSubmit(event) {
    if (event.type === 'click' && !event.target.closest?.('button[type="submit"],input[type="submit"]')) return;
    document.querySelectorAll(selector).forEach(function (field) { sanitizeField(field, true); });
  }
  document.addEventListener('submit', sanitizeBeforeSubmit, true);
  document.addEventListener('click', sanitizeBeforeSubmit, true);
  function initializeFields() { document.querySelectorAll(selector).forEach(addHelp); }
  new MutationObserver(initializeFields).observe(document.documentElement, { childList: true, subtree: true });
  initializeFields();
}());
